'use client';

import { TypeSettingsWithoutUnresolvableLinksResponse } from '@/features/contentful/_generated';
import { getLabel as getLabelUtil } from '@/utils/getLabel';
import createSettingsObject from '@/utils/createSettingsObject';
import { createContext, useContext, useMemo } from 'react';

interface ISettings {
    [key: string]: string | ISettings;
}

interface ISettingsContext {
    contentfulSettings: ISettings;
    lang: string;
    getLabel: (key: string, labelName: string, componentSettings?: ISettings) => string;
}

export const SettingsContext = createContext<ISettingsContext | undefined>(undefined);

interface ISettingsProvider {
    children: React.ReactNode;
    settings?: TypeSettingsWithoutUnresolvableLinksResponse[];
    lang: string;
}

export const SettingsProvider = ({ children, settings, lang }: ISettingsProvider) => {
    const contentfulSettings: ISettings = useMemo(() => createSettingsObject(settings), [settings]);

    const getLabel = useMemo(() => {
        return (key: string, labelName: string, componentSettings?: ISettings) => {
            return getLabelUtil(key, labelName, componentSettings || contentfulSettings);
        };
    }, [contentfulSettings]);

    const contextValue = useMemo(
        () => ({
            contentfulSettings,
            lang,
            getLabel,
        }),
        [contentfulSettings, lang, getLabel],
    );

    return <SettingsContext.Provider value={contextValue}>{children}</SettingsContext.Provider>;
};

export const useSettingsContext = () => {
    const context = useContext(SettingsContext);

    if (!context) {
        throw new Error('useSettingsContext must be used within a SettingsProvider');
    }

    return context;
};
